import type { CSSProperties, ReactNode } from 'react'

import type { ActivityOutcome, EnvironmentStatus } from '../../api/types'
import type { OperatorRoleName } from '../../auth/roles'
import {
  activityOutcomeTone,
  environmentStatusTone,
  operatorRoleTone,
  toneVisual,
  type StatusTone,
} from '../../theme/statusTone'

// StatusChip — ATC-109 / `ATC-ADR-018` anchor 4.
//
// TINT BACKGROUND + HEX DOT + DARK INK LABEL. Never coloured text.
//
// ATC's chips are coloured text today, and porting the contract's status hexes
// to `color:` literally would ship two AA failures: success is 3.30:1 and
// warning 3.19:1 on white, both under 4.5:1. That is not a contract defect —
// the hexes are published under `contrastAssertions.graphical` as "success dot
// / bar on surface", and Flight Deck never hits it because a Fluent tinted
// Badge handles the foreground for it. ATC HAS NO FLUENT, which is exactly why
// the contract publishes both a Fluent name and a hex.
//
// A chip must sit on a white surface (`Panel`). On the warm canvas the dots
// fall to 2.98 / 2.88 and miss even the 3:1 graphical floor.

export interface StatusChipProps {
  /**
   * The tone. `null` renders the toneless variant — an outlined chip with no
   * status colour, for the role badge's `none` case.
   */
  tone: StatusTone | null
  /** The status in words. Colour is never the sole carrier of meaning. */
  children: ReactNode
}

/**
 * The base chip. Prefer the wrappers below at a call site that has a wire value
 * — they route through `statusTone.ts`, which is where the mapping and both
 * exhaustiveness guards live.
 */
export function StatusChip({ tone, children }: StatusChipProps) {
  if (tone === null) {
    return <span className="rx-chip rx-chip-toneless">{children}</span>
  }

  const visual = toneVisual(tone)
  // Custom properties are not in React's CSSProperties index signature; the
  // cast is the standard escape hatch and keeps the values off a class name, so
  // a new tone never needs a new CSS rule.
  const style = {
    '--rx-chip-tint': visual.tint,
    '--rx-chip-dot': visual.hex,
  } as CSSProperties

  return (
    <span className="rx-chip" style={style} data-tone={tone}>
      {/* Decorative: the label states the status in words. */}
      <span className="rx-chip-dot" aria-hidden="true" />
      <span>{children}</span>
    </span>
  )
}

/** Customer-environment lifecycle status: `active` / `suspended` / `revoked`. */
export function EnvironmentStatusChip({ status }: { status: EnvironmentStatus }) {
  return <StatusChip tone={environmentStatusTone(status)}>{status}</StatusChip>
}

/**
 * Activity-feed outcome: `success` / `denied` / `not_found` / `error`.
 *
 * The label keeps the raw wire value, underscore and all. `not_found` is what
 * the operator will grep the logs for, and this feed is a debugging surface.
 */
export function ActivityOutcomeChip({ outcome }: { outcome: ActivityOutcome }) {
  return <StatusChip tone={activityOutcomeTone(outcome)}>{outcome}</StatusChip>
}

/**
 * The operator's role badge. NOT A STATUS CHIP — an identity label that happens
 * to reuse the chip shape. `operator` is neutral; `none` is toneless, because
 * that condition is already stated in words by the full-width notice the layout
 * renders instead of the page.
 */
export function RoleBadge({ role }: { role: OperatorRoleName }) {
  return <StatusChip tone={operatorRoleTone(role)}>{role}</StatusChip>
}
